import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import '../css/TimeProgressBar.css';
import LoadingSpinner from './LoadingSpinner';

const TimeProgressBar = ({ startDate, endDate, currentDate, setCurrentDate, setViewOption, loading, currentTotals }) => {
    const dataStartDate = dayjs(new Date(2021, 0, 1, 0, 0, 0, 0));
    const dataEndDate = dayjs().subtract(1, 'day');

    const [playing, setPlaying] = useState(false);
    const progressBarRef = useRef(null);
    const intervalRef = useRef(null);
    const [showMenu, setShowMenu] = useState(false);

    const scaleFactor = 0.5;

    const getLeftPosition = (date, referenceStart, referenceEnd) => {
        if (!date || !referenceStart || !referenceEnd) {
            return 0;
        }

        const total = (referenceEnd.diff(referenceStart)) * scaleFactor;
        const progressTime = (date.diff(referenceStart)) * scaleFactor;
        return Math.min((progressTime / total) * 100, 100);  // Ensure position is not greater than 100%
    };

    useEffect(() => {
        if (playing && !loading) {
            intervalRef.current = setInterval(() => {
                setCurrentDate((current) => {
                    const newCurrentDate = dayjs(current).add(1, 'day');
                    if (newCurrentDate.add(1, 'day').isAfter(dayjs(endDate))) {  // Stop one day before endDate
                        clearInterval(intervalRef.current);
                        return dayjs(endDate).subtract(1, 'day').toDate(); // Convert back to native Date
                    }
                    if (!newCurrentDate.isSame(dayjs(current), 'day')) {
                        return newCurrentDate.toDate(); // Convert back to native Date
                    }
                    return current;
                });
            }, 100);
        } else {
            clearInterval(intervalRef.current);
        }
        return () => clearInterval(intervalRef.current);
    }, [playing, loading, endDate, setCurrentDate]);

    useEffect(() => {
        if (dayjs(currentDate).add(1, 'day').isAfter(dayjs(endDate))) {
            setPlaying(false);
            setCurrentDate(dayjs(endDate).subtract(1, 'day').toDate());
        }
    }, [currentDate, endDate, setCurrentDate]);

    const updateDateFromPosition = (position) => {
        const progressBar = progressBarRef.current;
        const progressBarRect = progressBar.getBoundingClientRect();
        const progress = (position - progressBarRect.left) / progressBarRect.width;
        const newCurrentDate = dayjs(dataStartDate).add(progress * (dataEndDate.diff(dataStartDate)), 'millisecond');
        const boundedDate = newCurrentDate.add(1, 'day').isAfter(dayjs(endDate)) ? dayjs(endDate).subtract(1, 'day') : newCurrentDate;
        setCurrentDate(boundedDate.hour(0).minute(0).second(0).millisecond(0).toDate()); // Convert back to native Date
    };

    const handleMouseDown = (e) => {
        setPlaying(false);
        updateDateFromPosition(e.clientX);
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = (e) => {
        updateDateFromPosition(e.clientX);
    };

    const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    };

    const handleMenuOptionClick = (option) => {
        setViewOption(option);
        if (!loading) {
            setPlaying(true);
        }
        setShowMenu(false);
    };

    return (
        <div className="time-progress-bar">
            {/* Play Button - Positioned on the left side */}
            <button className="time-progress-play-button" onClick={() => {
                if (!playing && !loading) {
                    setShowMenu(!showMenu);
                } else {
                    setPlaying(false);
                }
            }}>
                {playing ? (
                    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                        <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"></path>
                    </svg>
                ) : (
                    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                        <path d="M8 5v14l11-7z"></path>
                    </svg>
                )}
            </button>
            
            {/* Play Options Menu - Positioned relative to the button */}
            {showMenu && (
                <div className={`play-options-menu ${showMenu ? 'visible' : ''} z-10`}>
                    <div className="menu-option" onClick={() => handleMenuOptionClick('cumulative')}>
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" style={{ marginRight: '0.5rem' }}>
                            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                        </svg>
                        Cumulative
                    </div>
                    <div className="menu-option" onClick={() => handleMenuOptionClick('regular')}>
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" style={{ marginRight: '0.5rem' }}>
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                        Daily
                    </div>
                </div>
            )}
            
            <div className="progress-container" ref={progressBarRef}>
                
                <div className="progress-bar"
                     style={{ left: `${getLeftPosition(dayjs(startDate), dataStartDate, dataEndDate)}%`, width: `${getLeftPosition(dayjs(currentDate), dataStartDate, dataEndDate) - getLeftPosition(dayjs(startDate), dataStartDate, dataEndDate)}%` }}></div>

                {/* Start Date Flag */}
                <div className="start-date-flag" style={{ left: `${getLeftPosition(dayjs(startDate), dataStartDate, dataEndDate)}%` }}>
                    <div className="flag-label">{dayjs(startDate).format('YYYY/MM/DD')}</div>
                    <div className="flag-pole"></div>
                </div>

                {/* China Flag */}
                <div className="china-flag" style={{ left: `${getLeftPosition(dayjs(new Date(2021, 5, 1, 0, 0, 0, 0)), dataStartDate, dataEndDate)}%` }}>
                    <div className="flag-label">China Bans Crypto Mining</div>
                    <div className="flag-pole"></div>
                </div>

                {/* End Date Flag */}
                <div className="end-date-flag" style={{ left: `${getLeftPosition(dayjs(endDate), dataStartDate, dataEndDate)}%` }}>
                    <div className="flag-label">{dayjs(endDate).format('YYYY/MM/DD')}</div>
                    <div className="flag-pole"></div>
                </div>

                <div
                    className="current-date-marker"
                    style={{ left: `${getLeftPosition(dayjs(currentDate), dataStartDate, dataEndDate)}%` }}
                    onMouseDown={handleMouseDown}
                >
                    <div className="current-date-triangle"></div>
                    <span className="current-date-label">
                      {currentDate ? dayjs(currentDate).format('YYYY/MM/DD') : (
                        <LoadingSpinner variant="dots" size="small" text="" />
                      )}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default TimeProgressBar;